import { Button, Input, Typography } from '@mgmresorts/wcl-lab';
import dayjs from 'dayjs';
import { navigate } from 'hookrouter';
import qs from 'query-string';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { KnownError } from '../../../api/errors';
import { MlifeVerificationRequest, mlifeVerificationRequest } from '../../../api/fetch/mlife-activation';
import { Alert } from '../../../components/ui/Alert';
import { Container } from '../../../components/ui/Container';
import { BirthdayInput } from '../../../components/ui/Inputs/BirthdayInput';
import { NavigationLink } from '../../../components/ui/Link';
import { Logo } from '../../../components/ui/Logo';
import { PageLayout } from '../../../components/ui/PageLayout';
import { useBirthdayInput } from '../../../hooks/useBirthdayInput';
import { useContent } from '../../../hooks/useContent';
import { useFieldValidation } from '../../../hooks/useFieldValidation';
import { useInput } from '../../../hooks/useInput';
import { captureEvent, captureException } from '../../../monitoring/logrocket';
import { useEvent } from '../../../utils/analytics/useEvent';
import { MaxLengths } from '../../../utils/constants';
import { PersistentParametersContext } from '../../../utils/persistParameterContext';
import { UserAccountContext } from '../../../utils/userAccountContext';
import { birthdayValidator, emailValidator, mlifeNumberValidator } from '../../../utils/validators';
import { StyledContent } from '../MlifeActivation.styled';
import { MlifeActivationContext } from '../MlifeActivationContext';

export function MlifeActivation() {
  const { email: userEmail } = useContext(UserAccountContext);
  const { email: emailAddress, mlifeNumber: mlifeNumberValue } = qs.parse(window.location.search) as {
    email: string;
    mlifeNumber: string;
  };
  if ((!emailAddress || !mlifeNumberValue) && !userEmail) {
    navigate('/', true);
  }
  const currentEmail = emailAddress || userEmail || '';
  const trackEvent = useEvent();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setErrorState] = useState<null | string>(null);
  const [trackedErrors, setTrackedErrors] = useState<string[]>([]);
  const { setActivationRequest, setStep } = useContext(MlifeActivationContext);
  const { getContentByPath } = useContent();
  const { validateField, invalidFields } = useFieldValidation([
    emailValidator,
    mlifeNumberValidator,
    birthdayValidator,
  ]);
  const { bind: bindEmail, value: email } = useInput(currentEmail, 'email', undefined, validateField);
  const { bind: bindBirthday, value: birthday } = useBirthdayInput('', 'birthday', undefined, validateField);
  const { bind: bindMlife, value: mlifeNumber } = useInput(
    mlifeNumberValue || '',
    'mlifeNumber',
    undefined,
    (field, value) => validateField(field, value, false),
  );

  const { params } = useContext(PersistentParametersContext);

  const fieldError = useCallback(
    (field: string): string | undefined =>
      invalidFields.includes(field) ? getContentByPath(`mlifeActivation.fields[${field}].error`) : undefined,
    [getContentByPath, invalidFields],
  );

  const canProcessMlifeActivation = useMemo(() => {
    return !!(email && birthday && !invalidFields.length);
  }, [email, birthday, invalidFields.length]);

  const requestMlifeActivation = useCallback(async () => {
    try {
      setIsLoading(true);

      const partnerData =
        params.partner && params.tokenKey
          ? { name: params.partner as string, tokenKey: params.tokenKey as string }
          : undefined;

      const verificationRequestBody: MlifeVerificationRequest = {
        login: email,
        dob: dayjs(birthday).format('YYYY-MM-DD'),
        partner: partnerData,
      };

      captureEvent('Activation', {
        email,
        partner: String(params.partner),
      });

      if (mlifeNumber) {
        verificationRequestBody.mlifeNumber = mlifeNumber;
      }

      const { activationKid } = await mlifeVerificationRequest(verificationRequestBody);

      setActivationRequest({
        login: email,
        birthday: dayjs(birthday).format('YYYY-MM-DD'),
        mlifeNumber,
        activationKid,
      });

      setErrorState(null);
      setIsLoading(false);

      await trackEvent(
        'identity – activate m life - credentials submit success',
        'activate m life credentials submit success',
        'amlifesubmitsuccess',
        {
          activateState: 'success',
          mlifenumberInput: mlifeNumber ? 'true' : 'false',
        },
        true,
      );

      setErrorState('message');

      setStep('verify');
    } catch (err) {
      setIsLoading(false);
      let message: string;
      if (err instanceof KnownError) {
        message = err.message;
      } else {
        message = getContentByPath('mlifeActivation.errors.default');
      }

      setErrorState(message);
      await trackEvent(
        'identity – activate m life - credentials submit error',
        'activate m life credentials submit error',
        'mlifesubmitfailure',
        {
          activateState: 'failed',
          errorDisplayed: message,
          mlifenumberInput: 'true',
        },
        true,
      );
      captureException(err as Error, 'activate', {
        username: email,
        mlifeNumber,
        step: 'verification-request',
      });
    }
  }, [
    params.partner,
    params.tokenKey,
    email,
    birthday,
    mlifeNumber,
    setActivationRequest,
    trackEvent,
    setStep,
    getContentByPath,
  ]);

  useEffect(() => {
    ['email', 'mlifeNumber', 'birthday'].forEach(field => {
      if (invalidFields.includes(field) && !trackedErrors.includes(field)) {
        trackEvent(
          'identity – activate m life - credentials input error',
          'activate m life credentials input error',
          'amlifeinputerror',
          {
            errorDisplayed: getContentByPath(`mlifeActivation.fields[${field}].error`),
            errorField: getContentByPath(`mlifeActivation.fields[${field}].title`),
          },
        );
      }
    });
    setTrackedErrors(invalidFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getContentByPath, invalidFields, trackEvent]);

  return (
    <StyledContent>
      <PageLayout
        testId='route-/activate'
        title={getContentByPath('mlifeActivation.title')}
        description={getContentByPath('mlifeActivation.description')}
      >
        <Container height='auto'>
          <Logo className='identity__page-logo' />

          <Typography className='identity__page-heading' variant='h1' testId='page-title'>
            {getContentByPath('mlifeActivation.title')}
          </Typography>

          {error && <Alert text={error} testId='mlifeActivation-error' />}

          <Input
            label={getContentByPath('mlifeActivation.fields.email.title')}
            id='email'
            testId='email'
            disabled
            aria-label={getContentByPath('mlifeActivation.fields.email.title')}
            className={`identity__field identity__field-email ${fieldError('email') ? 'with-error' : ''}`}
            error={!!fieldError('email')}
            hint={fieldError('email')}
            {...bindEmail}
            maxLength={MaxLengths.Email}
            domProps={{ 'data-lpignore': true }}
          />

          <BirthdayInput
            label={getContentByPath('mlifeActivation.fields.birthday.title')}
            id='birthDay'
            testId='birthDay'
            disabled={isLoading}
            aria-label={getContentByPath('mlifeActivation.fields.birthday.label')}
            hint={fieldError('birthday') || getContentByPath('mlifeActivation.fields.birthday.hint')}
            className={`identity__field identity__field-birthday ${fieldError('birthday') ? 'with-error' : ''}`}
            error={!!fieldError('birthday')}
            {...bindBirthday}
          />

          <Input
            label={getContentByPath('mlifeActivation.fields.mlifeNumber.title')}
            id='mlifeNumber'
            testId='mlifeNumber'
            disabled={isLoading}
            aria-label={getContentByPath('mlifeActivation.fields.mlifeNumber.label')}
            error={!!fieldError('mlifeNumber')}
            hint={fieldError('mlifeNumber')}
            className={`identity__field identity__field-mlifenumber ${fieldError('mlifeNumber') ? 'with-error' : ''}`}
            {...bindMlife}
          />

          <Button
            testId='activate-button'
            sticky
            ariaLabel={getContentByPath('mlifeActivation.ctaSection.label')}
            onClick={requestMlifeActivation}
            onKeyPress={e => {
              e.preventDefault();
              if (e.key === 'Enter') {
                requestMlifeActivation();
              }
            }}
            className='identity__form-submit'
            disabled={!canProcessMlifeActivation}
            loading={isLoading}
          >
            <Typography>{getContentByPath('mlifeActivation.ctaSection.title')}</Typography>
          </Button>

          <NavigationLink
            className='identity__secondary-action center'
            data-testid='mlifeActivation-back-link'
            path='/'
            onClick={async () => {
              await trackEvent(
                'identity – m life activation - back to sign in',
                'back to sign in',
                'mlabacktosignin',
                {},
                true,
              );
            }}
          >
            <svg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M12.85 4.95293L5.71004 12.1029H20.5V13.1029H5.71004L12.85 20.2529L12.15 20.9529L3.79004 12.6029L12.15 4.25293L12.85 4.95293Z'
                fill='#373AE5'
              />
            </svg>

            {getContentByPath('mlifeActivation.ctaSection.backToSignIn')}
          </NavigationLink>
        </Container>
      </PageLayout>
    </StyledContent>
  );
}

MlifeActivation.displayName = 'MlifeActivation';
